/**
 * Hook for managing conversation history between user and AI assistant
 * Provides state management and utility functions for message handling
 */

import { useState, useCallback } from 'react';

interface Message {
    id: string;
    content: string;
    type: 'user' | 'assistant';
    timestamp: Date;
}

export const useConversationHistory = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [showHistory, setShowHistory] = useState(false);
    const [isListening, setIsListening] = useState(false);

    /**
     * Add a new message to the conversation history
     * @param message - The message content to add
     * @param type - Whether the message is from 'user' or 'assistant'
     * @param timestamp - Optional timestamp for the message (defaults to current time)
     */
    const addMessage = useCallback((message: string, type: 'user' | 'assistant', timestamp: Date = new Date()) => {
        setMessages(prev => [...prev, {
            id: `${Date.now()}-${Math.random()}`,
            content: message,
            type,
            timestamp
        }]);
    }, []);

    /**
     * Clear all messages from the conversation history
     */
    const clearHistory = useCallback(() => {
        setMessages([]);
    }, []);

    /**
     * Toggle the visibility of the conversation history
     */
    const toggleHistory = useCallback(() => {
        setShowHistory(prev => !prev);
    }, []);

    /**
     * Set the listening state for showing placeholder
     */
    const setListeningState = useCallback((listening: boolean) => {
        setIsListening(listening);
    }, []);

    return {
        messages,
        showHistory,
        isListening,
        addMessage,
        clearHistory,
        toggleHistory,
        setListeningState
    };
};
